import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, TextField, Theme } from '@mui/material';
import { ProgramAttributes } from '@loyalty/shared-common/gamification-api-client';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faPen } from '@fortawesome/pro-solid-svg-icons/faPen';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../shared/api-client/ApiClientProvider';

const useStyles = makeStyles<Theme>(({ transitions }) => {
  const transition = transitions.create('opacity', {
    duration: transitions.duration.shorter,
  });

  return {
    name: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
    },
    button: {
      fontSize: 18,
      height: 'auto',
      opacity: 0,
      transition,
      '&:hover': {
        opacity: 1,
      },
    },
  };
});

interface Props {
  program: ProgramAttributes;
  onSaved: (program: ProgramAttributes) => void;
}

interface FormValue {
  name: string;
}

export function EditableProgramName({ program, onSaved }: Props): React.ReactElement {
  const classes = useStyles();
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValue>();

  const [editable, setEdtitable] = useState(false);

  const { gamificationApi } = useApi();

  const startEdition = () => setEdtitable(true);
  const stopEdition = () => setEdtitable(false);

  const handleSave = (data: FormValue) => {
    gamificationApi
      .updateProgram(program.id, { ...program, name: data.name })
      .then(freshProgram => {
        notify(t('shared.actions.saved'), 'success');
        onSaved(freshProgram);
      })
      .catch(error => {
        notify(t('programs.edit.dialog.errors.save', { error }), 'error');
      })
      .finally(() => {
        stopEdition();
      });
  };

  useEffect(() => {
    reset(program);
  }, [program]);

  return (
    <>
      {!editable && (
        <span className={classes.name}>
          <span>{program.name}</span>
          <IconButton className={classes.button} onClick={startEdition}>
            <FontAwesomeIcon size="xs" icon={faPen} />
          </IconButton>
        </span>
      )}
      {editable && (
        <form onSubmit={handleSubmit(handleSave)}>
          <span className={classes.name}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  autoFocus
                />
              )}
            />
            <IconButton type="submit">
              <FontAwesomeIcon size="xs" icon={faCheck} />
            </IconButton>
            <IconButton onClick={stopEdition}>
              <FontAwesomeIcon size="xs" icon={faTimes} />
            </IconButton>
          </span>
        </form>
      )}
    </>
  );
}
